import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface FAQItem {
  q: string;
  a: string;
}

const DEFAULT_FAQS: FAQItem[] = [
  {
    q: 'What types of properties does AARAN HOMES offer?',
    a: 'We deal in farmhouses, NA (non-agricultural) plots, agricultural land, residential plots, and second homes across Lonavala, Karjat, Kolad and other weekend-destination locations near Mumbai and Pune.',
  },
  {
    q: 'Are your plots and properties legally verified?',
    a: 'Yes. Every property listed on our site goes through a title and documentation check before it is published, and full legal paperwork is shared with buyers before booking.',
  },
  {
    q: 'Can I visit a property before making a decision?',
    a: 'Absolutely. Reach out via the "Contact Us" page, WhatsApp, or the inquiry form on any property page and our team will arrange a site visit at a time that works for you.',
  },
  {
    q: 'What is the difference between Featured and Exclusive properties?',
    a: 'Featured properties are popular, high-demand listings. Exclusive properties are limited-inventory or premium listings available only through AARAN HOMES.',
  },
  {
    q: 'Do you provide brochures with pricing and layout details?',
    a: 'Yes, most properties have a downloadable brochure with layout, pricing and amenity details. Look for the "Download Brochure" button on the property page.',
  },
  {
    q: 'What financing or payment plan options are available?',
    a: 'Payment plans vary by project. Our team will walk you through the available options — including structured installments for select projects — during your inquiry.',
  },
  {
    q: 'How do I get updates on new property launches?',
    a: 'Submit your details through our Contact page or any inquiry form, and our team will keep you updated on new launches and exclusive pre-launch pricing.',
  },
];

interface FAQProps {
  title?: string;
  subtitle?: string;
  items?: FAQItem[];
  className?: string;
}

export const FAQ: React.FC<FAQProps> = ({
  title = 'Frequently Asked Questions',
  subtitle = 'Answers to what buyers ask us most often',
  items = DEFAULT_FAQS,
  className = '',
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className={`py-16 sm:py-20 bg-gray-50 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">{title}</h2>
          <p className="text-lg text-gray-600">{subtitle}</p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {items.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 sm:py-5 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="font-semibold text-gray-900">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: isOpen ? '300px' : '0' }}
                >
                  <p className="px-5 sm:px-6 pb-4 sm:pb-5 text-gray-600 leading-relaxed">{item.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
